import { emit, type HapticIntent } from '../postMessage';
import { isHostListeningForHaptics } from './useEnableHapticsListener';

export { useEnableHapticsListener } from './useEnableHapticsListener';

/**
 * Tactile feedback for the reveal's key beats via the Vibration API.
 * Feature-detected and wrapped in try/catch, so it's a silent no-op on
 * unsupported devices (desktop, all iOS). In practice every reveal beat is
 * downstream of a tap (Shuffle / Open Next Pack / Accept Buyback), so the
 * recent user activation that some browsers require for `vibrate` is present.
 *
 * Once the host opts in (`enable_haptics`), every beat is also posted to it
 * as a `haptic` event: iOS browsers have no Vibration API at all, so an app
 * WebView around us is the only thing that can make the phone buzz there.
 */

export type HapticBeat = 'tick' | 'land' | 'win' | 'bigWin';

const PATTERNS: Record<HapticBeat, number | number[]> = {
  // Light tap — a reel tile passing / a digit settling.
  tick: 8,
  // The reel's mechanical "plonk" landing.
  land: 18,
  // Buyback payout / win.
  win: [0, 35, 25, 55],
  // Rare / high-value payout — heavier, two-stage.
  bigWin: [0, 45, 30, 70, 30, 100],
};

const HOST_INTENTS: Record<HapticBeat, HapticIntent> = {
  tick: 'light',
  land: 'medium',
  win: 'success',
  bigWin: 'heavy',
};

export function haptic(beat: HapticBeat): void {
  if (isHostListeningForHaptics()) emit('haptic', { intent: HOST_INTENTS[beat] });
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.vibrate !== 'function'
  ) {
    return;
  }
  try {
    navigator.vibrate(PATTERNS[beat]);
  } catch {
    // Some engines throw without a recent user gesture — ignore.
  }
}

/** Win buzz that scales up for a bigger payout. */
export function winHaptic(amount: number): void {
  haptic(amount >= 100 ? 'bigWin' : 'win');
}
